import { z } from 'zod';

import { isValidPhoneDisplay } from '@/modules/ui';

const DOB_PATTERN = /^\d{2} \/ \d{2} \/ \d{4}$/;
const MIN_AGE = 18;
const MIN_BIRTH_YEAR = 1900;

// Parses the display mask `MM / DD / YYYY` into a Date. Returns `null` if any
// component is out of range — including impossible calendar dates like
// 02 / 31 / 1990, which `new Date(1990, 1, 31)` would silently roll forward.
function parseBirthday(value: string): Date | null {
  const match = /^(\d{2}) \/ (\d{2}) \/ (\d{4})$/.exec(value);
  if (!match) return null;
  const [, mm, dd, yyyy] = match;
  const month = Number(mm);
  const day = Number(dd);
  const year = Number(yyyy);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

function yearsSince(date: Date, now: Date = new Date()): number {
  let age = now.getFullYear() - date.getFullYear();
  const monthDelta = now.getMonth() - date.getMonth();
  const dayDelta = now.getDate() - date.getDate();
  // Birthday this year hasn't happened yet — they're still last year's age.
  if (monthDelta < 0 || (monthDelta === 0 && dayDelta < 0)) age -= 1;
  return age;
}

export const accountSchema = z.object({
  firstName: z.string().trim().min(1, 'Enter your legal first name'),
  lastName: z.string().trim().min(1, 'Enter your legal last name'),
  email: z.string().trim().pipe(z.email('Enter a valid email address')),
  phone: z
    .string()
    .trim()
    .refine(isValidPhoneDisplay, 'Enter a valid phone number as (xxx) xxx-xxxx'),
  // Format gate first, then domain rules: must be a real calendar date,
  // year ≥ 1900, and the user must be at least 18. Mirrors Sparta's
  // useAccountCreationFormik birthday validation.
  birthday: z
    .string()
    .trim()
    .regex(DOB_PATTERN, 'Enter your date of birth as MM / DD / YYYY')
    .superRefine((value, ctx) => {
      const date = parseBirthday(value);
      if (!date) {
        ctx.addIssue({ code: 'custom', message: 'Enter a valid date of birth' });
        return;
      }
      if (date.getFullYear() < MIN_BIRTH_YEAR) {
        ctx.addIssue({ code: 'custom', message: 'Enter a valid date of birth' });
        return;
      }
      if (date.getTime() > Date.now()) {
        ctx.addIssue({ code: 'custom', message: 'Date of birth cannot be in the future' });
        return;
      }
      if (yearsSince(date) < MIN_AGE) {
        ctx.addIssue({ code: 'custom', message: `You must be at least ${MIN_AGE} years old` });
      }
    }),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must include at least 1 uppercase letter')
    .regex(/\d/, 'Password must include at least 1 number')
    .regex(/[^A-Za-z0-9]/, 'Password must include at least 1 symbol'),
  // Map of legal-document anchor uuids → checkbox value. The form populates
  // this dynamically once GET /legal-documents/ resolves (one entry per
  // checkbox group). z.literal(true) rejects any unchecked box with a
  // per-uuid error path so the corresponding checkbox highlights.
  agreements: z.record(z.string(), z.literal(true, { error: 'Required' })),
  sms: z.boolean().optional(),
});

// SSO mode hides firstName / lastName / email / phone (the SSO provider owns
// them — the SSO submit only PATCHes DOB / consents / language), so the schema
// must not validate them: a thin SSO profile missing any of those would
// otherwise fail Continue silently on a field the user can't see. They're
// relaxed to a plain `z.string()` (accepts `''`, no min/email/phone rules) —
// note: NOT `.optional()`, which would widen the inferred type to
// `string | undefined` and diverge from the shared FormValues. The
// still-visible birthday + agreements keep their full rules. Password is
// likewise not collected/sent (accepts `''`).
export const ssoAccountSchema = accountSchema.extend({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phone: z.string(),
  password: z.string(),
});

// SSO mode + PostHog flag enabled: password field is shown and required.
// firstName / lastName / email / phone remain relaxed (still hidden);
// only password gets the full validation rules restored.
export const ssoWithPasswordAccountSchema = ssoAccountSchema.extend({
  password: accountSchema.shape.password,
});

// PURL account creation collects email + password + optional phone + consents
// (+ SMS). Name comes from the b2b eligibility record the token resolves to, and
// the DOB was confirmed on the dedicated entry screen — so those are hidden and
// relaxed to a plain `z.string()` (NOT `.optional()`, which would widen the
// inferred type away from the shared FormValues). email / phone / password /
// agreements keep their full rules (phone stays optional — empty is valid).
export const purlAccountSchema = accountSchema.extend({
  firstName: z.string(),
  lastName: z.string(),
  birthday: z.string(),
});

// All flows (normal / SSO / PURL) collect the same field set — the per-flow
// schemas only relax validation on hidden fields, so they infer the identical
// value shape. Shared so the per-flow screens + useAccountForm agree on it.
export type AccountFormValues = z.infer<typeof accountSchema>;

// The PURL DOB-verification entry screen collects only the date of birth (reusing
// the full birthday validation). Its own form, separate from AccountFormValues.
export const purlDobSchema = z.object({
  birthday: accountSchema.shape.birthday,
});
export type PurlDobFormValues = z.infer<typeof purlDobSchema>;
